export function currency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}

export function currencyCompact(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(2)}%`;
}

import {
  formatCalendarDate,
  formatDateInTz,
  formatTimeInTz,
} from "@/utils/datetime";

/**
 * An INSTANT (`Sep 3, 2026`), rendered in the VIEWER's zone.
 *
 * 🔴 `tz` IS REQUIRED, AND THE ABSENCE OF A DEFAULT IS THE POINT. This
 * previously read `tz: string = FALLBACK_TIMEZONE`, so all nine call sites —
 * none of which passed one — rendered in `America/Phoenix` for every viewer on
 * earth, SILENTLY. A defaulted zone parameter cannot be got wrong loudly: the
 * call compiles, the render looks plausible, and the only tell is a viewer in
 * another zone disagreeing about the day for seven hours a night.
 *
 * Required makes that a COMPILE ERROR at every future call site, which is
 * strictly better than a ratchet — a ratchet enumerating today's callers cannot
 * fail for a file that does not exist yet, and that file is the regression.
 *
 * Pass `useDateFormatter().tz` from a component. `string | null` (not
 * `| undefined`) is deliberate: null is a real "the user set no preference"
 * value that the formatters already fold to the fallback, but a caller cannot
 * satisfy it by simply omitting the argument.
 */
export function shortDate(iso: string | null | undefined, tz: string | null): string {
  if (!iso) return "—";
  return formatDateInTz(iso, tz) || "—";
}

/**
 * A WALL-CALENDAR date (`Sep 3, 2026`), rendered in UTC.
 *
 * Use this — not `shortDate` — for a day a human picked or a domain date. Such
 * a value is stored at UTC midnight, so rendering it in any negative-offset
 * zone (`America/Phoenix` is UTC-7) shows the PREVIOUS day. That is the
 * commission payment-date defect, and it is why this helper takes no `tz` at
 * all: a wall-calendar day means the same day everywhere, so there is no
 * viewer zone to honour.
 *
 * `shortDate` stays correct — and stays the right choice — for genuine instants
 * (`createdAt`, `recognizedAt`, an audit `timestamp`, and `revenueReceivedDate`,
 * which is assigned `candidates.Max(c => c.RecognizedAt)` server-side and is an
 * instant despite its name).
 */
export function calendarDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatCalendarDate(iso) || "—";
}

/**
 * An INSTANT with its time of day (`Sep 3, 2026 · 6:30 PM`), rendered in the
 * VIEWER's zone.
 *
 * 🔴 `tz` IS REQUIRED HERE FOR THE SAME REASON IT IS ON `shortDate`, AND THIS
 * FUNCTION IS WHY THAT COMMENT NEEDED WRITING TWICE. It kept
 * `tz: string = FALLBACK_TIMEZONE` for months after its sibling was hardened
 * twenty-five lines above — the exact shape that comment condemns, in the same
 * file, under it. All four call sites passed one argument, so every commission
 * audit entry, every discussion comment and every period lock stamp rendered in
 * `America/Phoenix` for every viewer on earth.
 *
 * It survived because the ratchet could not see it: `src/test/viewerZoneSupply.test.ts`
 * walks the tree for one-argument calls to tz-aware helpers, and its `HELPERS`
 * array listed `shortDate` and not this. A scan that cannot see its subject
 * reports a clean tree, which is indistinguishable from a clean tree. `datetime`
 * is in that array now, so the compile error and the ratchet both catch the next
 * one.
 *
 * Pass `useDateFormatter().tz`. Use `calendarDate` instead — never a zone — for
 * a day a human picked; see its own doc for why the two are not
 * interchangeable.
 */
export function datetime(iso: string | null | undefined, tz: string | null): string {
  if (!iso) return "—";
  const date = formatDateInTz(iso, tz);
  const time = formatTimeInTz(iso, tz);
  if (!date) return "—";
  return `${date} · ${time}`;
}

export function truncate(s: string | null | undefined, max = 80): string {
  if (!s) return "";
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}
